import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "~/components/ui/button";
import { ConditionalNavLink } from "~/components/ui/ConditionalNavLink";
import {
  designItems,
  developmentItems,
  requirementItems,
  systemProposalItems,
  projectTop as top,
} from "~/config/menuItems";
import { TOAST_MESSAGES } from "~/constants/messages";
import { ProjectApi } from "~/functions/api/project";
import ArrowDropDownClose from "~/images/arrow_drop_down_close.svg?react";
import ArrowDropDownOpen from "~/images/arrow_drop_down_open.svg?react";
import ChartIcon from "~/images/chart.svg?react";
import Close from "~/images/close.svg?react";
import ContractInfoIcon from "~/images/contract_info.svg?react";
import DocumentIcon from "~/images/document.svg?react";
import KintoneIcon from "~/images/kintone-logo.svg?react";
import LightbulbIcon from "~/images/lightbulb.svg?react";
import OrganizationIcon from "~/images/organization.svg?react";
import ProjectHomeIcon from "~/images/project_home.svg?react";
import ReverseIcon from "~/images/reverse.svg?react";
import TableIcon from "~/images/table.svg?react";
import TopIcon from "~/images/top.svg?react";
import type { Projects } from "~/interface/project";
import { setCurrentProject } from "~/slices/auth";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

// アコーディオンメニュー項目の型定義
type AccordionMenu = {
  title: string;
  isOpen: boolean;
  toggleOpen: () => void;
  items: typeof requirementItems | typeof designItems | typeof developmentItems;
};

// アイコンマッピング
const iconMap: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  document: DocumentIcon,
  table: TableIcon,
  chart: ChartIcon,
  top: TopIcon,
  reverse: ReverseIcon,
  kintone: KintoneIcon,
  "project-home": ProjectHomeIcon,
  organization: OrganizationIcon,
  "contract-info": ContractInfoIcon,
  lightbulb: LightbulbIcon,
};

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // アコーディオンメニューの状態を管理
  const [isSystemProposalItemsOpen, setIsSystemProposalItemsOpen] = useState(true);
  const [isRequirementItemsOpen, setIsRequirementItemsOpen] = useState(true);
  const [isDesignItemsMenuOpen, setDesignItemsMenuOpen] = useState(true);
  const [isDevelopmentItemsMenuOpen, setIsDevelopmentItemsMenuOpen] = useState(true);

  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [isMobileView, setMobileView] = useState(false);
  const { organization } = useSelector((state: RootState) => state.auth);
  const isStarterPlan = organization?.plan && organization.plan.type === "starter";
  const contentRef = useRef<HTMLUListElement | null>(null);
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const currentProject = useSelector((state: RootState) => state.auth.currentProject);
  const isProjectDataLoading = useSelector((state: RootState) => state.project?.isLoading);

  // プロジェクト切替用
  const [isProjectMenuOpen, setIsProjectMenuOpen] = useState(false);
  const [projects, setProjects] = useState<Projects[]>([]);
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const [hasFetchedProjects, setHasFetchedProjects] = useState(false);
  const projectMenuRef = useRef<HTMLDivElement | null>(null);
  // プロジェクト切替中スケルトン制御
  const [isProjectSwitching, setIsProjectSwitching] = useState(false);
  const [switchTargetProjectId, setSwitchTargetProjectId] = useState<string | null>(null);
  const [seenLoadingAfterSwitch, setSeenLoadingAfterSwitch] = useState(false);

  // アコーディオンメニューの定義
  const accordionMenus: AccordionMenu[] = [
    {
      title: "システム概要",
      isOpen: isSystemProposalItemsOpen,
      toggleOpen: () => setIsSystemProposalItemsOpen(!isSystemProposalItemsOpen),
      items: systemProposalItems,
    },
    {
      title: "要件定義",
      isOpen: isRequirementItemsOpen,
      toggleOpen: () => setIsRequirementItemsOpen(!isRequirementItemsOpen),
      items: requirementItems,
    },
    {
      title: "基本設計",
      isOpen: isDesignItemsMenuOpen,
      toggleOpen: () => setDesignItemsMenuOpen(!isDesignItemsMenuOpen),
      items: designItems,
    },
    {
      title: "開発",
      isOpen: isDevelopmentItemsMenuOpen,
      toggleOpen: () => setIsDevelopmentItemsMenuOpen(!isDevelopmentItemsMenuOpen),
      items: developmentItems,
    },
  ];

  // サイドバーの開閉
  const openSideBar = () => setSidebarVisible(true);
  const closeSideBar = () => setSidebarVisible(false);

  const handleFeedbackClick = () => {
    const feedbackUrl = "https://novelworks.chobiit.me/public/p_add_record.html?appId=1510";
    const confirmed = window.confirm(`別のページに遷移します。よろしいですか？\n${feedbackUrl}`);
    if (confirmed) {
      window.open(feedbackUrl, "_blank");
    }
  };

  // モバイルビューの検出
  useEffect(() => {
    const checkMobileView = () => {
      const isMobile = window.innerWidth <= 768;
      setMobileView(isMobile);

      if (!isMobile) {
        setSidebarVisible(true);
      } else {
        setSidebarVisible(false);
      }
    };
    checkMobileView();
    window.addEventListener("resize", checkMobileView);
    return () => window.removeEventListener("resize", checkMobileView);
  }, []);

  useEffect(() => {
    if (isSidebarVisible) {
      document.body.classList.add("sidebar-visible");
    } else {
      document.body.classList.remove("sidebar-visible");
    }
    return () => {
      document.body.classList.remove("sidebar-visible");
    };
  }, [isSidebarVisible]);

  // プロジェクトメニューの外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (projectMenuRef.current && !projectMenuRef.current.contains(e.target as Node)) {
        setIsProjectMenuOpen(false);
      }
    };
    if (isProjectMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProjectMenuOpen]);

  // システム概要書のメニュー項目を生成する関数
  const renderSystemProposalItems = (items: typeof systemProposalItems) => {
    return items.map((item) => {
      const IconComponent = iconMap[item.icon];
      const contentId = currentProject?.outputs?.[item.type]?.id;
      const isGenerating = currentProject?.outputs?.[item.type]?.isGenerating;

      const handleItemClick = (e: React.MouseEvent) => {
        if (!contentId && item.type !== "summary" && item.type !== "cost-effectiveness") {
          e.preventDefault();
          if (isGenerating) {
            toast.info(TOAST_MESSAGES.LAYOUT.SIDEBAR_GENERATING(item.name));
          } else {
            toast.error(TOAST_MESSAGES.LAYOUT.SIDEBAR_NOT_GENERATED(item.name));
          }
        }
        if (isMobileView) closeSideBar();
      };

      if (item.type === "system-proposal") {
        return (
          <li className={styles["link"]} key={item.type}>
            {contentId ? (
              <NavLink
                className={({ isActive }) => (isActive ? "active" : "")}
                to={`/${currentUser.organization_id}/project/${currentProject.id}/system-proposal/${contentId}`}
                onClick={handleItemClick}
              >
                {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
                {item.name}
              </NavLink>
            ) : (
              <button type="button" className="disabled-link" onClick={handleItemClick}>
                {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
                {item.name}
              </button>
            )}
          </li>
        );
      } else {
        const shouldShowAsEnabled =
          item.type === "cost-effectiveness"
            ? Boolean(currentProject?.outputs?.["estimation"]?.id)
            : Boolean(contentId);

        return (
          <li className={styles["link"]} key={item.type}>
            {shouldShowAsEnabled ? (
              <NavLink
                className={({ isActive }) => (isActive ? "active" : "")}
                to={`/${currentUser.organization_id}/project/${currentProject.id}/${item.type}${item.type === "cost-effectiveness" ? "" : contentId ? `/${contentId}` : ""}`}
                onClick={handleItemClick}
              >
                {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
                {item.name}
              </NavLink>
            ) : (
              <button type="button" className="disabled-link" onClick={handleItemClick}>
                {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
                {item.name}
              </button>
            )}
          </li>
        );
      }
    });
  };

  // プロジェクト一覧の取得（メニュー開くタイミング）
  const openProjectMenu = async () => {
    const willOpen = !isProjectMenuOpen;
    setIsProjectMenuOpen(willOpen);
    if (
      willOpen &&
      !hasFetchedProjects &&
      !isProjectLoading &&
      currentUser?.id &&
      currentUser?.organization_id
    ) {
      setIsProjectLoading(true);
      const api = new ProjectApi();
      const res = await api.getAllProjects(currentUser.organization_id);
      if (res?.projects) {
        setProjects(res.projects);
      }
      setIsProjectLoading(false);
      setHasFetchedProjects(true);
    }
  };

  const handleProjectSelect = async (projectId: string) => {
    if (!currentUser?.id || !currentUser?.organization_id) return;
    try {
      setIsProjectSwitching(true);
      setSeenLoadingAfterSwitch(false);
      setSwitchTargetProjectId(projectId);
      const api = new ProjectApi();
      const project = await api.get(currentUser.organization_id, projectId);
      if (project) {
        dispatch(setCurrentProject(project));
        // メンバー取得はProjectTop側でprojectId変更を検知して実行
        navigate(`/${currentUser.organization_id}/project/${projectId}/dashboard`);
        setIsProjectMenuOpen(false);
        if (isMobileView) closeSideBar();
      }
    } catch (e) {
      console.error(e);
      setIsProjectSwitching(false);
      setSwitchTargetProjectId(null);
      setSeenLoadingAfterSwitch(false);
    }
  };

  // 切替完了の検知
  useEffect(() => {
    if (!isProjectSwitching) return;
    // 新しいプロジェクトでロード開始を検知
    if (isProjectDataLoading) {
      setSeenLoadingAfterSwitch(true);
    }
    // ロードを一度でも見た後に、対象IDでロード終了したら切替完了
    if (
      switchTargetProjectId &&
      currentProject?.id === switchTargetProjectId &&
      seenLoadingAfterSwitch &&
      isProjectDataLoading === false
    ) {
      setIsProjectSwitching(false);
      setSwitchTargetProjectId(null);
      setSeenLoadingAfterSwitch(false);
    }
  }, [
    isProjectSwitching,
    switchTargetProjectId,
    currentProject?.id,
    isProjectDataLoading,
    seenLoadingAfterSwitch,
  ]);

  // フォールバック: 2秒で強制解除（ネットワーク不調時のハング防止）
  useEffect(() => {
    if (!isProjectSwitching) return;
    const t = setTimeout(() => {
      setIsProjectSwitching(false);
      setSwitchTargetProjectId(null);
      setSeenLoadingAfterSwitch(false);
    }, 2000);
    return () => clearTimeout(t);
  }, [isProjectSwitching]);

  const handleOpenProjectList = () => {
    if (!currentUser?.organization_id) return;
    navigate(`/${currentUser.organization_id}/home`);
    setIsProjectMenuOpen(false);
    if (isMobileView) closeSideBar();
  };

  // 要件定義書と基本設計書のメニュー項目を生成する関数
  const renderDocumentItems = (items: typeof requirementItems | typeof designItems) => {
    return items.map((item) => {
      const IconComponent = iconMap[item.icon];
      // コンテンツIDの取得
      const contentId = currentProject?.outputs?.[item.type]?.id;
      const isGenerating = currentProject?.outputs?.[item.type]?.isGenerating;

      const handleItemClick = (e: React.MouseEvent) => {
        if (!contentId) {
          e.preventDefault();
          e.stopPropagation();
          if (isGenerating) {
            toast.info(TOAST_MESSAGES.LAYOUT.SIDEBAR_GENERATING(item.name));
          } else {
            toast.error(TOAST_MESSAGES.LAYOUT.SIDEBAR_NOT_GENERATED(item.name));
          }
        }
        if (isMobileView) closeSideBar();
      };

      return (
        <li className={styles["link"]} key={item.type}>
          {isStarterPlan ? (
            <span className="disabled-link">
              {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
              {item.name}
            </span>
          ) : contentId ? (
            <NavLink
              className={({ isActive }) => (isActive ? "active" : "")}
              to={`/${currentUser.organization_id}/project/${currentProject.id}/${item.type}/${contentId}`}
              onClick={handleItemClick}
            >
              {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
              {item.name}
            </NavLink>
          ) : (
            <button type="button" className="disabled-link" onClick={handleItemClick}>
              {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
              {item.name}
            </button>
          )}
        </li>
      );
    });
  };

  // 開発メニュー項目を生成する関数
  const renderDevelopmentItems = (items: typeof developmentItems) => {
    return items.map((item) => {
      const IconComponent = iconMap[item.icon];
      return (
        <li className={styles["link"]} key={item.type}>
          <ConditionalNavLink
            isDisabled={isStarterPlan}
            to={
              currentProject?.id
                ? `/${currentUser.organization_id}/project/${currentProject.id}/${item.type}`
                : "/signin"
            }
            onClick={() => isMobileView && closeSideBar()}
          >
            {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
            {item.name}
          </ConditionalNavLink>
        </li>
      );
    });
  };

  // アコーディオンメニューを生成する関数
  const renderAccordionMenu = (menu: AccordionMenu, index: number) => {
    return (
      <React.Fragment key={index}>
        <button type="button" className={styles["sidebarItemTitle"]} onClick={menu.toggleOpen}>
          <span className={styles["menu-title"]}>{menu.title}</span>
          <p className={styles["sidebarArrowDropDown"]}>
            {menu.isOpen ? <ArrowDropDownOpen /> : <ArrowDropDownClose />}
          </p>
        </button>
        <ul
          className={`${styles["accordion-content"]} ${menu.isOpen ? styles["open"] : ""}`}
          ref={contentRef}
          style={{
            maxHeight: menu.isOpen ? "1000px" : "0px",
          }}
        >
          {index === 0 && renderSystemProposalItems(menu.items)}
          {index === 1 && renderDocumentItems(menu.items)}
          {index === 2 && renderDocumentItems(menu.items)}
          {index === 3 && renderDevelopmentItems(menu.items)}
        </ul>
      </React.Fragment>
    );
  };

  return (
    <div>
      {/* サイドバーが閉じているときのトグルボタン */}
      {!isSidebarVisible && (
        <div className={styles["sidebar-toggle"]}>
          <button
            type="button"
            aria-label="メニューを開く"
            aria-controls="tutorial-sidebar"
            aria-expanded={false}
            onClick={openSideBar}
            data-tour-allow="true"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M6.83496 3.99992C6.38353 4.00411 6.01421 4.0122 5.69824 4.03801C5.31232 4.06954 5.03904 4.12266 4.82227 4.20012L4.62207 4.28606C4.18264 4.50996 3.81498 4.85035 3.55859 5.26848L3.45605 5.45207C3.33013 5.69922 3.25006 6.01354 3.20801 6.52824C3.16533 7.05065 3.16504 7.71885 3.16504 8.66301V11.3271C3.16504 12.2712 3.16533 12.9394 3.20801 13.4618C3.25006 13.9766 3.33013 14.2909 3.45605 14.538L3.55859 14.7216C3.81498 15.1397 4.18266 15.4801 4.62207 15.704L4.82227 15.79C5.03904 15.8674 5.31234 15.9205 5.69824 15.9521C6.01398 15.9779 6.383 15.986 6.83398 15.9902L6.83496 3.99992ZM18.165 11.3271C18.165 12.2493 18.1653 12.9811 18.1172 13.5702C18.0745 14.0924 17.9916 14.5472 17.8125 14.9648L17.7295 15.1415C17.394 15.8 16.8834 16.3511 16.2568 16.7353L15.9814 16.8896C15.5157 17.1268 15.0069 17.2285 14.4102 17.2773C13.821 17.3254 13.0893 17.3251 12.167 17.3251H7.83301C6.91071 17.3251 6.17898 17.3254 5.58984 17.2773C5.06757 17.2346 4.61294 17.1508 4.19531 16.9716L4.01855 16.8896C3.36014 16.5541 2.80898 16.0434 2.4248 15.4169L2.27051 15.1415C2.03328 14.6758 1.93158 14.167 1.88281 13.5702C1.83468 12.9811 1.83496 12.2493 1.83496 11.3271V8.66301C1.83496 7.74072 1.83468 7.00898 1.88281 6.41985C1.93157 5.82309 2.03329 5.31432 2.27051 4.84856L2.4248 4.57317C2.80898 3.94666 3.36012 3.436 4.01855 3.10051L4.19531 3.0175C4.61285 2.83843 5.06771 2.75548 5.58984 2.71281C6.17898 2.66468 6.91071 2.66496 7.83301 2.66496H12.167C13.0893 2.66496 13.821 2.66468 14.4102 2.71281C15.0069 2.76157 15.5157 2.86329 15.9814 3.10051L16.2568 3.25481C16.8833 3.63898 17.394 4.19012 17.7295 4.84856L17.8125 5.02531C17.9916 5.44285 18.0745 5.89771 18.1172 6.41985C18.1653 7.00898 18.165 7.74072 18.165 8.66301V11.3271ZM8.16406 15.995H12.167C13.1112 15.995 13.7794 15.9947 14.3018 15.9521C14.8164 15.91 15.1308 15.8299 15.3779 15.704L15.5615 15.6015C15.9797 15.3451 16.32 14.9774 16.5439 14.538L16.6299 14.3378C16.7074 14.121 16.7605 13.8478 16.792 13.4618C16.8347 12.9394 16.835 12.2712 16.835 11.3271V8.66301C16.835 7.71885 16.8347 7.05065 16.792 6.52824C16.7605 6.14232 16.7073 5.86904 16.6299 5.65227L16.5439 5.45207C16.32 5.01264 15.9796 4.64498 15.5615 4.3886L15.3779 4.28606C15.1308 4.16013 14.8165 4.08006 14.3018 4.03801C13.7794 3.99533 13.1112 3.99504 12.167 3.99504H8.16406C8.16407 3.99667 8.16504 3.99829 8.16504 3.99992L8.16406 15.995Z"></path>
            </svg>
          </button>
        </div>
      )}

      <div
        className={`${styles["sidebar"]} ${
          isSidebarVisible ? styles["visible"] : styles["hidden"]
        }`}
        data-tour-id="tutorial-sidebar"
        data-testid="sidebar"
      >
        {/* サイドバーが開いているときのトグルボタン */}
        {isSidebarVisible && (
          <div className={styles["sidebar-toggle-inside"]}>
            <button
              type="button"
              aria-label="メニューを閉じる"
              aria-controls="tutorial-sidebar"
              aria-expanded={true}
              onClick={closeSideBar}
              data-tour-allow="true"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M6.83496 3.99992C6.38353 4.00411 6.01421 4.0122 5.69824 4.03801C5.31232 4.06954 5.03904 4.12266 4.82227 4.20012L4.62207 4.28606C4.18264 4.50996 3.81498 4.85035 3.55859 5.26848L3.45605 5.45207C3.33013 5.69922 3.25006 6.01354 3.20801 6.52824C3.16533 7.05065 3.16504 7.71885 3.16504 8.66301V11.3271C3.16504 12.2712 3.16533 12.9394 3.20801 13.4618C3.25006 13.9766 3.33013 14.2909 3.45605 14.538L3.55859 14.7216C3.81498 15.1397 4.18266 15.4801 4.62207 15.704L4.82227 15.79C5.03904 15.8674 5.31234 15.9205 5.69824 15.9521C6.01398 15.9779 6.383 15.986 6.83398 15.9902L6.83496 3.99992ZM18.165 11.3271C18.165 12.2493 18.1653 12.9811 18.1172 13.5702C18.0745 14.0924 17.9916 14.5472 17.8125 14.9648L17.7295 15.1415C17.394 15.8 16.8834 16.3511 16.2568 16.7353L15.9814 16.8896C15.5157 17.1268 15.0069 17.2285 14.4102 17.2773C13.821 17.3254 13.0893 17.3251 12.167 17.3251H7.83301C6.91071 17.3251 6.17898 17.3254 5.58984 17.2773C5.06757 17.2346 4.61294 17.1508 4.19531 16.9716L4.01855 16.8896C3.36014 16.5541 2.80898 16.0434 2.4248 15.4169L2.27051 15.1415C2.03328 14.6758 1.93158 14.167 1.88281 13.5702C1.83468 12.9811 1.83496 12.2493 1.83496 11.3271V8.66301C1.83496 7.74072 1.83468 7.00898 1.88281 6.41985C1.93157 5.82309 2.03329 5.31432 2.27051 4.84856L2.4248 4.57317C2.80898 3.94666 3.36012 3.436 4.01855 3.10051L4.19531 3.0175C4.61285 2.83843 5.06771 2.75548 5.58984 2.71281C6.17898 2.66468 6.91071 2.66496 7.83301 2.66496H12.167C13.0893 2.66496 13.821 2.66468 14.4102 2.71281C15.0069 2.76157 15.5157 2.86329 15.9814 3.10051L16.2568 3.25481C16.8833 3.63898 17.394 4.19012 17.7295 4.84856L17.8125 5.02531C17.9916 5.44285 18.0745 5.89771 18.1172 6.41985C18.1653 7.00898 18.165 7.74072 18.165 8.66301V11.3271ZM8.16406 15.995H12.167C13.1112 15.995 13.7794 15.9947 14.3018 15.9521C14.8164 15.91 15.1308 15.8299 15.3779 15.704L15.5615 15.6015C15.9797 15.3451 16.32 14.9774 16.5439 14.538L16.6299 14.3378C16.7074 14.121 16.7605 13.8478 16.792 13.4618C16.8347 12.9394 16.835 12.2712 16.835 11.3271V8.66301C16.835 7.71885 16.8347 7.05065 16.792 6.52824C16.7605 6.14232 16.7073 5.86904 16.6299 5.65227L16.5439 5.45207C16.32 5.01264 15.9796 4.64498 15.5615 4.3886L15.3779 4.28606C15.1308 4.16013 14.8165 4.08006 14.3018 4.03801C13.7794 3.99533 13.1112 3.99504 12.167 3.99504H8.16406C8.16407 3.99667 8.16504 3.99829 8.16504 3.99992L8.16406 15.995Z"></path>
              </svg>
            </button>
          </div>
        )}

        <button type="button" className={styles["sidebar_close"]} onClick={closeSideBar}>
          <Close />
        </button>
        <div className={styles["menu"]}>
          {currentProject?.id === undefined ? (
            <div>
              <ul className={styles["menu-wrapper"]}>
                <li className={styles["link"]}>
                  <NavLink
                    className={({ isActive }) => (isActive ? "active" : "")}
                    to={`/${currentUser.organization_id}/home`}
                    data-testid="nav-projects"
                    onClick={() => isMobileView && closeSideBar()}
                  >
                    <ProjectHomeIcon className={styles["sidebar-icon"]} />
                    プロジェクト
                  </NavLink>
                </li>
                <li className={styles["link"]}>
                  <NavLink
                    className={({ isActive }) => (isActive ? "active" : "")}
                    to={`/${currentUser.organization_id}/organization/info`}
                    data-testid="nav-organization"
                    onClick={() => isMobileView && closeSideBar()}
                  >
                    <OrganizationIcon className={styles["sidebar-icon"]} />
                    組織
                  </NavLink>
                </li>
                <li className={styles["link"]}>
                  <NavLink
                    className={({ isActive }) => (isActive ? "active" : "")}
                    to={`/${currentUser.organization_id}/billings`}
                    onClick={() => isMobileView && closeSideBar()}
                  >
                    <ContractInfoIcon className={styles["sidebar-icon"]} />
                    支払いとプラン
                  </NavLink>
                </li>
              </ul>
            </div>
          ) : (
            <div className={styles["menu-wrapper"]}>
              <div className={styles["project-selector"]} ref={projectMenuRef}>
                {isProjectSwitching ? (
                  <div className={styles["project-selector__skeletonButton"]} />
                ) : (
                  <button
                    type="button"
                    className={styles["project-selector__button"]}
                    onClick={openProjectMenu}
                    data-tour-allow="true"
                  >
                    <span className={styles["project-selector__name"]} title={currentProject?.name}>
                      {currentProject?.name || "プロジェクト"}
                    </span>
                    <span className={styles["project-selector__icon"]}>
                      {isProjectMenuOpen ? <ArrowDropDownOpen /> : <ArrowDropDownClose />}
                    </span>
                  </button>
                )}
                {!isProjectSwitching && isProjectMenuOpen && (
                  <div className={styles["project-selector__menu"]}>
                    <ul>
                      {projects.slice(0, 5).map((p) => (
                        <li key={p.project.id}>
                          <button
                            type="button"
                            className={styles["project-selector__item"]}
                            onClick={() => handleProjectSelect(p.project.id)}
                            title={p.project.name}
                          >
                            {p.project.name}
                          </button>
                        </li>
                      ))}
                      {isProjectLoading && (
                        <>
                          <li key="skeleton-0">
                            <div className={styles["project-selector__skeletonItem"]} />
                          </li>
                          <li key="skeleton-1">
                            <div className={styles["project-selector__skeletonItem"]} />
                          </li>
                        </>
                      )}
                      {hasFetchedProjects && !isProjectLoading && projects.length === 0 && (
                        <li className={styles["project-selector__empty"]}>
                          プロジェクトがありません
                        </li>
                      )}
                    </ul>
                    <div className={styles["project-selector__footer"]}>
                      <button
                        type="button"
                        className={styles["project-selector__link"]}
                        onClick={handleOpenProjectList}
                      >
                        プロジェクト一覧へ
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <ul
                className={`${styles["accordion-content"]} ${styles["open"]}`}
                ref={contentRef}
                style={{ maxHeight: "1000px" }}
              >
                {isProjectSwitching
                  ? Array.from({ length: 8 }, (_, i) => (
                      <li key={`s-l-${i}`} className={styles["link"]}>
                        <div className={styles["sidebar-skeletonLine"]} />
                      </li>
                    ))
                  : top.map((item) => {
                      const IconComponent = iconMap[item.icon];
                      const basePath = currentProject?.id
                        ? `/${currentUser.organization_id}/project/${currentProject.id}${item.link}`
                        : "/signin";
                      const testId =
                        item.link === "/generate-status"
                          ? "nav-generation-status"
                          : item.link === "/dashboard"
                            ? "nav-dashboard"
                            : `nav-${item.link.replace("/", "")}`;

                      return (
                        <li className={styles["link"]} key={item.link}>
                          <NavLink
                            className={({ isActive }) => (isActive ? "active" : "")}
                            to={basePath}
                            data-testid={testId}
                            onClick={() => isMobileView && closeSideBar()}
                          >
                            {IconComponent && <IconComponent className={styles["sidebar-icon"]} />}
                            {item.name}
                          </NavLink>
                        </li>
                      );
                    })}
              </ul>

              {isProjectSwitching
                ? Array.from({ length: 10 }, (_, i) => (
                    <div key={`acc-s-${i}`} className={styles["sidebarItemTitle"]}>
                      <div className={styles["sidebar-skeletonLine"]} style={{ width: "70%" }} />
                    </div>
                  ))
                : accordionMenus.map(renderAccordionMenu)}
            </div>
          )}
          <div className={styles["menu-wrapper-feedback"]}>
            <div className={styles["feedback-content"]}>
              <h3>フィードバックを送る</h3>
              <p>REQXIGNについてのご意見・ご要望、不具合のご報告などお気軽にお送りください。</p>
            </div>
            <Button onClick={handleFeedbackClick} label="外部サイトへ" styleType="white-feedback" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
